import { Icon } from "@legal-500/legal500-design-system";
import styles from "./StatusBadge.module.css";
import { reviewStatusBadge } from "./reviewStatusBadge";

type StatusBadgeProps = {
  status: string | null | undefined;
  /** Draw the pencil that marks the badge as editable. Off everywhere it is read-only. */
  interactive?: boolean;
  /** Selects which status vocabulary to render. Defaults to the edition-status vocabulary. */
  vocabulary?: "review-status";
  /** The owning edition's status, which promotes an approved item to "Released" once it is live. */
  editionStatus?: string | null;
};

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  researching: { label: "Researching", className: "researching" },
  editing: { label: "Editing", className: "editing" },
  ready_for_release: { label: "Ready for Release", className: "readyForRelease" },
  complete: { label: "Complete", className: "complete" },
  live: { label: "Live", className: "live" },
  archived: { label: "Archived", className: "archived" },
  not_started: { label: "Not Started", className: "notStarted" },
  setup: { label: "Setup", className: "setup" },
};

function humaniseStatus(status: string): string {
  const spaced = status.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function getStatusLabel(status: string): string {
  return STATUS_CONFIG[status]?.label ?? humaniseStatus(status);
}

// `editing` maps to `reviewEditing` rather than the `editing` class above: that one is the blue
// BPA-workflow badge, and the review vocabulary needs orange.
const REVIEW_STATUS_CLASS_NAMES: Record<string, string> = {
  notStarted: "notStarted",
  inProgress: "inProgress",
  editing: "reviewEditing",
  approved: "approved",
  released: "released",
};

export function StatusBadge({
  status,
  interactive = false,
  vocabulary,
  editionStatus,
}: StatusBadgeProps) {
  const config =
    vocabulary === "review-status"
      ? (() => {
          const badge = reviewStatusBadge(status, editionStatus);
          return { label: badge.label, className: REVIEW_STATUS_CLASS_NAMES[badge.tone] };
        })()
      : (STATUS_CONFIG[status as string] ?? {
          label: humaniseStatus(status as string),
          className: "setup",
        });

  return (
    <span className={`${styles.badge} ${styles[config.className]}`}>
      {config.label}
      {interactive && (
        <Icon
          icon="edit"
          size={{ width: 0.6875, height: 0.6875 }}
          className={styles.affordance}
          aria-hidden
        />
      )}
    </span>
  );
}
